/**
 * stableOrder — freeze list row order during review.
 *
 * The task list re-sorts on every render from `sortBy`. When a task is edited
 * in place (e.g. priority low → high) its sort key changes, the row jumps to a
 * new position, and the scroll viewport is yanked away — the user loses their
 * place mid-review. This keeps existing rows in their established positions and
 * only adopts a fresh sort when `rebaseline` is true (the user changed the
 * view, sort field/direction, or search).
 *
 * - Unchanged members  → prior order preserved (edited objects kept in place).
 * - Removed tasks       → dropped.
 * - Genuinely-new tasks → appended at the end, in fresh order.
 */

import { useRef } from 'react';

export function stableOrder<T extends { id: string }>(
  fresh: T[],
  prevIds: string[],
  rebaseline: boolean,
): { items: T[]; ids: string[] } {
  if (rebaseline || prevIds.length === 0) {
    return { items: fresh, ids: fresh.map((t) => t.id) };
  }
  const byId = new Map(fresh.map((t) => [t.id, t]));
  const kept = prevIds
    .map((id) => byId.get(id))
    .filter((t): t is T => t != null);
  const seen = new Set(prevIds);
  const appended = fresh.filter((t) => !seen.has(t.id));
  const items = [...kept, ...appended];
  return { items, ids: items.map((t) => t.id) };
}

/**
 * React hook wrapper: returns `fresh` reordered to preserve prior positions,
 * rebaselining whenever `sig` changes. `sig` should encode everything that
 * legitimately re-sorts the list (view, sortBy, sortDirection, search, …).
 */
export function useStableOrder<T extends { id: string }>(fresh: T[], sig: string): T[] {
  const ref = useRef<{ sig: string; ids: string[] }>({ sig, ids: [] });
  const rebaseline = ref.current.sig !== sig;
  const { items, ids } = stableOrder(fresh, ref.current.ids, rebaseline);
  // Derived-state memo: safe to update during render (value depends only on
  // inputs; StrictMode double-invoke is idempotent — second pass sees the same
  // sig and ids and produces the same result).
  ref.current = { sig, ids };
  return items;
}
